/**
 * Milestone Promotion Templates
 * Celebrate user achievements and company milestones
 */

import { PromotionTemplate } from "./types";

export const MILESTONE_TEMPLATES: PromotionTemplate[] = [
  {
    id: "user-anniversary",
    name: "User Anniversary",
    category: "milestone",
    description: "Celebrate user's signup anniversary with a special offer",
    strategy: "Anniversary promotions make users feel valued. They're highly personalized and have excellent open rates. Works best with loyal users who might upgrade or refer friends.",
    icon: "GiftOutlined",
    color: "#EC4899",
    expectedOutcomes: [
      "High engagement from loyal users",
      "Increased retention",
      "Upgrade opportunities for free users",
    ],
    bestPractices: [
      "Personalize with user's name",
      "Mention specific anniversary date",
      "Thank them for being a customer",
      "Offer exclusive anniversary discount",
    ],
    tags: ["milestone", "anniversary", "personalized", "retention"],
    bestFor: ["SaaS with free tiers", "Community products", "Subscription services"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "festive",
      priority: 8,
      targetAudience: ["free", "paid"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "enterprise"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#EC4899",
        secondaryColor: "#8B5CF6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Happy Anniversary!",
          subheadline: "You've been with us for 1 year! Here's 30% off to celebrate.",
          ctaText: "Claim Anniversary Gift",
          ctaSecondaryText: "Maybe Later",
          badge: "ANNIVERSARY",
          terms: "*Valid for 7 days from your anniversary date.",
        },
      },
    },
  },
  {
    id: "usage-milestone",
    name: "Usage Milestone",
    category: "milestone",
    description: "Reward users who hit usage milestones",
    strategy: "Celebrating usage milestones (100 queries, 1000 generations, etc.) acknowledges user investment and creates upgrade opportunities for power users.",
    icon: "TrophyOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "Power user recognition",
      "Upgrade conversions from active users",
      "Increased engagement",
    ],
    bestPractices: [
      "Highlight their achievement",
      "Show specific usage stats",
      "Offer relevant upgrade path",
      "Make them feel like a VIP",
    ],
    tags: ["milestone", "usage", "power-user", "engagement"],
    bestFor: ["Usage-based products", "AI tools", "Creative software"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 7,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 35,
        currency: "USD",
        applicablePlans: ["pro"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#F59E0B",
        secondaryColor: "#EF4444",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 1,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "You're on Fire!",
          subheadline: "You've hit 100 AI generations! Unlock unlimited with 35% off Pro.",
          ctaText: "Go Unlimited",
          ctaSecondaryText: "Keep Free Plan",
          badge: "MILESTONE",
          terms: "*Special pricing for power users like you.",
        },
      },
    },
  },
  {
    id: "company-milestone",
    name: "Company Milestone",
    category: "milestone",
    description: "Celebrate company achievements with users",
    strategy: "Share your wins with users (1M users, funding, awards). Creates community feeling and provides a non-promotional reason for a sale.",
    icon: "RocketOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "Brand building",
      "Community engagement",
      "Natural promotion opportunity",
    ],
    bestPractices: [
      "Share genuine milestone",
      "Thank users for being part of it",
      "Offer celebratory discount",
      "Keep it authentic",
    ],
    tags: ["milestone", "company", "celebration", "community"],
    bestFor: ["Growing startups", "Community-focused products"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "banner",
      displayStyle: "festive",
      priority: 6,
      targetAudience: ["all"],
      discount: {
        discountType: "percentage",
        discountValue: 25,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#10B981",
        secondaryColor: "#3B82F6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 5,
        delaySeconds: 3,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "We Hit 1 Million Users!",
          subheadline: "Thanks to you! Celebrate with us - 25% off everything.",
          ctaText: "Join the Celebration",
          ctaSecondaryText: "Learn More",
          badge: "1M USERS",
          terms: "*Celebration pricing valid this week only.",
        },
      },
    },
  },
  {
    id: "product-launch",
    name: "Product Launch Special",
    category: "milestone",
    description: "Celebrate new feature/product launches with early adopter pricing",
    strategy: "Product launches create natural excitement. Early adopter discounts reward engaged users and drive initial adoption of new features.",
    icon: "RocketOutlined",
    color: "#6366F1",
    expectedOutcomes: [
      "Early feature adoption",
      "Feedback from engaged users",
      "Revenue from launch excitement",
    ],
    bestPractices: [
      "Highlight what's new",
      "Offer early adopter exclusive",
      "Create limited-time urgency",
      "Gather feedback in exchange",
    ],
    tags: ["milestone", "launch", "new-feature", "early-adopter"],
    bestFor: ["Feature-rich products", "Fast-iterating startups"],
    difficulty: "beginner",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 8,
      targetAudience: ["free", "paid"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro", "enterprise"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#6366F1",
        secondaryColor: "#8B5CF6",
        animation: "slide",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 100,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Just Launched!",
          subheadline: "Be among the first 100 to try our new feature. 40% off for early adopters.",
          ctaText: "Get Early Access",
          ctaSecondaryText: "See What's New",
          badge: "NEW",
          terms: "*Early adopter pricing for first 100 subscribers.",
        },
      },
    },
  },
];
